import {
  type MachineArt,
  SMALL_CRATE,
  SMALL_DRAIN,
  SMALL_FAUCET,
  SMALL_HARDHAT,
  SMALL_HOUSE,
  SMALL_HVAC,
  SMALL_JOB,
  SMALL_MULTIMETER,
  SMALL_RADIATOR,
  SMALL_SHOWER,
  SMALL_VAN,
  SmallMachine,
} from "@volca/ui/components/ui/empty-machine";
import { type TradeKey, tradeOf } from "@volca/ui/trades";
import type {
  ReportObjectArt,
  ReportObjectIcon,
} from "@/profitability/report-object";

const BY_ART: Record<ReportObjectArt, MachineArt> = {
  job: SMALL_JOB,
  hardhat: SMALL_HARDHAT,
  van: SMALL_VAN,
  crate: SMALL_CRATE,
  house: SMALL_HOUSE,
  hvac: SMALL_HVAC,
  radiator: SMALL_RADIATOR,
  faucet: SMALL_FAUCET,
  shower: SMALL_SHOWER,
  multimeter: SMALL_MULTIMETER,
  drain: SMALL_DRAIN,
};

const BY_TRADE: Record<TradeKey, MachineArt> = {
  hvac: SMALL_HVAC,
  heating: SMALL_RADIATOR,
  cooling: SMALL_HVAC,
  plumbing: SMALL_FAUCET,
  drains: SMALL_DRAIN,
  water_heater: SMALL_SHOWER,
  electrical: SMALL_MULTIMETER,
};

const BY_KIND: Record<ReportObjectIcon, MachineArt> = {
  job: SMALL_JOB,
  person: SMALL_HARDHAT,
  team: SMALL_VAN,
  vendor: SMALL_CRATE,
  place: SMALL_HOUSE,
  item: SMALL_CRATE,
};

/**
 * The small drawing a record is grounded by: the one its script named
 * (`object.art`). Without one, what its trade works on for a job or
 * business unit whose trade is known (an outdoor unit for HVAC, a
 * faucet for plumbing, a meter for electrical), else its kind: a work order
 * for a job, a hard hat for a technician, a van for a business unit, a crate
 * for a vendor, a house for a customer. `hints` are the names that may say
 * the trade, most specific first: a job's type ("Furnace install"), then its
 * business unit ("HVAC Install").
 */
export const keystoneArt = (
  icon: ReportObjectIcon,
  hints: (string | null | undefined)[],
  art?: ReportObjectArt,
): MachineArt => {
  if (art && BY_ART[art]) return BY_ART[art];
  const trade =
    icon === "job" || icon === "team"
      ? hints.map((hint) => tradeOf(hint)).find(Boolean)
      : undefined;
  return trade ? BY_TRADE[trade.key] : (BY_KIND[icon] ?? SMALL_JOB);
};

/**
 * A record's keystone, the way a map sits beside an order's customer: its
 * small machine at the head of the panel, standing on the name's baseline.
 * It is the record's mark, so the panel shows no other icon. Decorative.
 */
export const RecordKeystone = ({
  icon,
  hints,
  art,
}: {
  icon: ReportObjectIcon;
  hints: (string | null | undefined)[];
  /** The drawing the script named, which wins over any guess. */
  art?: ReportObjectArt;
}) => (
  <SmallMachine
    art={keystoneArt(icon, hints, art)}
    size={72}
    className="pointer-events-none -me-1 flex-none select-none"
  />
);
